import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  postReverseGirviDashboard,
  isErpApiSuccess,
  getErpRequestErrorMessage,
  getTenantBrandImageSrcWithFallback,
} from "../../erpClient";
import { getUserData } from "../const";
import { hasPermission, PERMISSIONS } from "../../utils/permissions";
import { formatGirviCurrency } from "../girvi/girviTheme";
import { useDashboardValuePrivacy } from "../../hooks/useDashboardValuePrivacy";
import PrivacyBlur from "../../components/PrivacyBlur";
import TransactionHistoryTable from "../girvi/TransactionHistoryTable";
import "../girvi/girvi.css";

function dashNum(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function ReverseGirviDashboard() {
  const navigate = useNavigate();
  const { valuesHidden } = useDashboardValuePrivacy();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [hasDashboardPermission, setHasDashboardPermission] = useState(
    () => hasPermission(PERMISSIONS.REVERSE_GIRVI_DASHBOARD_VIEW)
  );

  const canList = hasPermission(PERMISSIONS.REVERSE_GIRVI_VIEW);

  // Listen for permission changes in real-time
  useEffect(() => {
    const handlePermissionsUpdated = () => {
      setHasDashboardPermission(hasPermission(PERMISSIONS.REVERSE_GIRVI_DASHBOARD_VIEW));
    };

    window.addEventListener("permissionsUpdated", handlePermissionsUpdated);
    window.addEventListener("erpAuthUpdated", handlePermissionsUpdated);

    return () => {
      window.removeEventListener("permissionsUpdated", handlePermissionsUpdated);
      window.removeEventListener("erpAuthUpdated", handlePermissionsUpdated);
    };
  }, []);

  const fetchDashboardData = async () => {
    if (!hasDashboardPermission) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const user = getUserData();
      const branchIdRaw = user?.branch_id ?? user?.branchId ?? 1;
      const branch_id =
        branchIdRaw !== "" && branchIdRaw != null
          ? Number(branchIdRaw) || 1
          : 1;

      const data = await postReverseGirviDashboard({
        branch_id,
      });

      if (isErpApiSuccess(data) || data?.message === "radha") {
        setDashboardData(data.data ?? data);
      } else {
        toast.error(data?.message || "Failed to fetch dashboard data");
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      toast.error(
        getErpRequestErrorMessage(error, "Failed to fetch dashboard data"),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasDashboardPermission) {
      fetchDashboardData();
    } else {
      setLoading(false);
    }
  }, [hasDashboardPermission]);

  useEffect(() => {
    const handleApiUrlChange = () => {
      if (hasDashboardPermission) {
        fetchDashboardData();
      }
    };

    window.addEventListener("apiUrlChanged", handleApiUrlChange);

    return () => {
      window.removeEventListener("apiUrlChanged", handleApiUrlChange);
    };
  }, [hasDashboardPermission]);

  // If user does NOT have girvi dashboard permission, show the tenant logo as a landing page
  if (!hasDashboardPermission) {
    const logoUrl = getTenantBrandImageSrcWithFallback("logo");

    return (
      <div className="body-wrapper">
        <div
          className="d-flex align-items-center justify-content-center"
          style={{ minHeight: "calc(100vh - 140px)" }}
        >
          <div className="text-center">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt="Company Logo"
                style={{
                  maxWidth: "280px",
                  maxHeight: "280px",
                  objectFit: "contain",
                  opacity: 0.85,
                }}
              />
            ) : (
              <div
                style={{
                  width: "120px",
                  height: "120px",
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto",
                  opacity: 0.7,
                }}
              >
                <i
                  className="ti ti-building-store"
                  style={{ fontSize: "3rem", color: "#fff" }}
                ></i>
              </div>
            )}
            <p
              className="text-muted mt-3"
              style={{ fontSize: "0.95rem" }}
            >
              Welcome! Use the sidebar to navigate.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const remaining = dashNum(dashboardData?.remaining_amt);
  const principal = dashNum(dashboardData?.principal_taken);
  const recoveredPct =
    principal > 0
      ? Math.min(100, Math.max(0, ((principal - remaining) / principal) * 100))
      : 0;

  return (
    <div className="body-wrapper">
      <div className="container-fluid py-4">
        <div className="d-flex flex-column gap-4">
        <div className="row g-4">
          <div className="col-12">
            <section className="girvi-ledger">
              <div className="girvi-ledger-main">
                <p className="girvi-ledger-kicker">Remaining liability</p>
                <p className="girvi-ledger-hero">
                  {loading ? (
                    <span className="girvi-ledger-skel" />
                  ) : (
                    <PrivacyBlur hidden={valuesHidden}>
                      {formatGirviCurrency(remaining)}
                    </PrivacyBlur>
                  )}
                </p>
                <div
                  className="girvi-ledger-track"
                  role="progressbar"
                  aria-valuenow={Math.round(recoveredPct)}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label="Principal returned"
                >
                  <span style={{ width: loading ? "0%" : `${recoveredPct}%` }} />
                </div>
              </div>
              <div className="girvi-ledger-aside">
                <div className="girvi-ledger-stat">
                  <span>Active deposits</span>
                  <strong>
                    {loading ? "—" : dashNum(dashboardData?.active_deposits)}
                  </strong>
                </div>
                <div className="girvi-ledger-stat">
                  <span>Closed</span>
                  <strong>
                    {loading ? "—" : dashNum(dashboardData?.closed_deposits)}
                  </strong>
                </div>
                <div className="girvi-ledger-stat">
                  <span>Principal taken</span>
                  <strong>
                    {loading ? (
                      "—"
                    ) : (
                      <PrivacyBlur hidden={valuesHidden}>
                        {formatGirviCurrency(principal)}
                      </PrivacyBlur>
                    )}
                  </strong>
                </div>
                <div className="girvi-ledger-stat">
                  <span>Interest paid</span>
                  <strong>
                    {loading ? (
                      "—"
                    ) : (
                      <PrivacyBlur hidden={valuesHidden}>
                        {formatGirviCurrency(dashboardData?.paid_interest)}
                      </PrivacyBlur>
                    )}
                  </strong>
                </div>
                <div className="girvi-ledger-stat">
                  <span>Deposits</span>
                  <strong>
                    {loading ? "—" : dashNum(dashboardData?.total_deposits)}
                  </strong>
                </div>
                <div className="girvi-ledger-stat">
                  <span>Returned</span>
                  <strong>
                    {loading ? "—" : `${recoveredPct.toFixed(0)}%`}
                  </strong>
                </div>
              </div>
            </section>
          </div>
        </div>

        <div className="row g-4">
          <div className="col-12">
            <TransactionHistoryTable
              loading={loading}
              data={dashboardData?.today_transaction || []}
              idField="reverse_girvi_no"
              idLabel="Number"
              onViewAll={canList ? () => navigate("/reversegirvitable") : undefined}
            />
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}

export default ReverseGirviDashboard;
